import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildClientCompetitiveReportQuery,
  buildCompetitivePerformanceFetchKey,
  extractCompetitiveReportsArray,
  normalizeCompetitivePerformancePayload,
} from "../../lib/utils/competitivePerformanceReport"
import {
  formatSocialPerformanceDate,
  parseSocialPerformanceDateParts,
} from "../../lib/utils/socialPerformanceReport"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

function competitiveArgKey(arg = {}) {
  const { clientId, startDate, endDate, rangeMode } = arg
  return buildCompetitivePerformanceFetchKey(
    clientId,
    formatSocialPerformanceDate(startDate),
    formatSocialPerformanceDate(endDate),
    rangeMode,
  )
}

async function fetchAllCompetitivePages(clientId, query) {
  const collected = []
  let endpoint = `report/clients/${clientId}/competitive?${query}`
  for (let page = 0; page < 20; page += 1) {
    const data = await get(endpoint)
    collected.push(...extractCompetitiveReportsArray(data))
    const next = data?.paging?.next
    if (!next) break
    endpoint = next
  }
  return collected
}

/**
 * GET /api/v1/report/clients/{clientId}/competitive
 * Month: ?aggregation_mode=&period=month&year=&month=&limit=
 * Custom range: ?aggregation_mode=&start_date=&end_date=&limit=
 */
export const fetchClientCompetitivePerformance = createAsyncThunk(
  "clientCompetitivePerformance/fetch",
  async ({ clientId, startDate, endDate, rangeMode, socialNetwork } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatSocialPerformanceDate(startDate)
      const end = formatSocialPerformanceDate(endDate)
      const parts = parseSocialPerformanceDateParts(end || start)
      if (!start || !end || (rangeMode !== "custom" && !parts)) {
        return rejectWithValue("startDate and endDate are required")
      }

      const fetchKey = buildCompetitivePerformanceFetchKey(clientId, start, end, rangeMode)
      const query = buildClientCompetitiveReportQuery(
        rangeMode === "custom"
          ? {
              period: "custom",
              startDate: start,
              endDate: end,
              aggregationMode: "paid_and_organic",
              socialNetwork,
            }
          : {
              period: "month",
              year: parts.year,
              month: parts.month,
              aggregationMode: "paid_and_organic",
              socialNetwork,
            },
      )
      const rows = await fetchAllCompetitivePages(clientId, query)

      return {
        reports: normalizeCompetitivePerformancePayload({ data: rows }),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey,
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch competitive performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      const key = competitiveArgKey(arg)
      return !skipIfFetchKeyLoaded(getState().clientCompetitivePerformance, key)
    },
  },
)

const initialState = {
  reports: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const nextKey = competitiveArgKey(action.meta.arg || {})
  const keepStale =
    nextKey === state.fetchedFetchKey &&
    Array.isArray(state.reports) &&
    state.reports.length > 0

  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStale) {
    state.fetchedFetchKey = null
    state.reports = []
  }
}

function isStaleFetch(action, state) {
  return competitiveArgKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")
}

const clientCompetitivePerformanceSlice = createSlice({
  name: "clientCompetitivePerformance",
  initialState,
  reducers: {
    clearClientCompetitivePerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientCompetitivePerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientCompetitivePerformance.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.reports = action.payload?.reports ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientCompetitivePerformance.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch competitive performance"
        state.reports = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientCompetitivePerformance } = clientCompetitivePerformanceSlice.actions
export default clientCompetitivePerformanceSlice.reducer
